import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { AuthenticatedUser, TokenPair } from '@utazzo/shared';

/**
 * Auth store. Holds ONLY auth state - tokens + current user.
 *
 * - Persisted to localStorage (refresh survives page reload).
 * - Refresh-on-401 happens in apiClient interceptor (W2).
 * - Server state for everything else lives in TanStack Query, never here.
 */

interface AuthState {
  user: AuthenticatedUser | null;
  tokens: TokenPair | null;
  isAuthenticated: boolean;
  setSession: (user: AuthenticatedUser, tokens: TokenPair) => void;
  setTokens: (tokens: TokenPair) => void;
  /**
   * Refresh the cached user without touching tokens. `user` is written at
   * login and persisted, so an admin changing someone's role left every
   * `role === ...` check in the app reading the OLD role until that person
   * logged out. See `useSyncSessionUser`.
   */
  setUser: (user: AuthenticatedUser) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      setSession: (user, tokens) => set({ user, tokens, isAuthenticated: true }),
      setTokens: (tokens) => set((s) => ({ ...s, tokens })),
      setUser: (user) => set((s) => ({ ...s, user })),
      clear: () => set({ user: null, tokens: null, isAuthenticated: false }),
    }),
    {
      name: 'utazzo.auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

/**
 * Synchronous access for non-React code (axios interceptors).
 */
export const getAccessToken = (): string | null =>
  useAuthStore.getState().tokens?.accessToken ?? null;
